import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  isPushSupported, isIOS, isStandaloneDisplay,
  getCurrentSubscription, subscribeToPush, unsubscribeFromPush,
} from "@/lib/push";

export default function PushToggle() {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState("default");
  const iosNotStandalone = isIOS() && !isStandaloneDisplay();

  const refresh = useCallback(async () => {
    if (!isPushSupported()) { setSupported(false); return; }
    setSupported(true);
    setPermission(typeof Notification !== "undefined" ? Notification.permission : "default");
    const sub = await getCurrentSubscription();
    setSubscribed(!!sub);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (!supported) return null;

  const onEnable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      setSubscribed(true);
      setPermission("granted");
      toast.success("Push notifications enabled");
    } catch (e) {
      toast.error(e.message || "Couldn't enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      toast.success("Push notifications disabled");
    } catch (e) {
      toast.error(e.message || "Couldn't disable notifications");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="push-toggle-card"
      className="rounded-lg border p-5"
      style={{ backgroundColor: "#fff", borderColor: "rgba(0,0,0,0.08)" }}
    >
      <div className="flex items-start gap-4">
        <div
          className="p-2 rounded-md shrink-0"
          style={{
            backgroundColor: subscribed ? "rgba(111,125,85,0.14)" : "rgba(31,93,52,0.12)",
            color: subscribed ? "var(--moss)" : "var(--terracotta)",
          }}
        >
          {subscribed ? <Bell size={18} /> : <BellOff size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline" style={{ color: "var(--ink-soft)" }}>Phone alerts</div>
          <div className="font-serif text-2xl mt-1">
            {subscribed ? "Notifications on." : "Get pinged the moment it happens."}
          </div>
          <p className="text-sm mt-2" style={{ color: "var(--ink-soft)" }}>
            {subscribed
              ? "You'll get a phone-native alert for every approval, application, and rating — even when AddVendTour isn't open."
              : "Turn on push and get a real notification on your phone or laptop for new applications, approvals, and messages."}
          </p>

          {iosNotStandalone && !subscribed && (
            <div
              data-testid="push-ios-hint"
              className="mt-4 flex items-start gap-2 text-xs rounded-md p-3"
              style={{
                backgroundColor: "rgba(31,93,52,0.06)",
                border: "1px solid rgba(31,93,52,0.25)",
                color: "var(--ink-soft)",
              }}
            >
              <Smartphone size={14} className="mt-0.5 shrink-0" style={{ color: "var(--terracotta)" }} />
              <div>
                <strong>On iPhone?</strong> Tap the Share icon → <em>Add to Home Screen</em>, then
                open AddVendTour from the new icon and try again. Apple requires push to be enabled
                from installed home-screen apps only.
              </div>
            </div>
          )}

          {permission === "denied" && !iosNotStandalone && (
            <div
              data-testid="push-denied-hint"
              className="mt-4 text-xs rounded-md p-3"
              style={{
                backgroundColor: "rgba(31,93,52,0.06)",
                border: "1px solid rgba(31,93,52,0.25)",
                color: "var(--terracotta)",
              }}
            >
              Notifications are blocked in your browser settings. Open Site Settings → Notifications
              to re-allow them, then click Enable.
            </div>
          )}

          <div className="mt-4">
            {subscribed ? (
              <Button
                type="button"
                data-testid="push-disable"
                onClick={onDisable}
                disabled={busy}
                variant="outline"
                className="rounded-full"
              >
                {busy ? "Disabling…" : "Disable notifications"}
              </Button>
            ) : (
              <Button
                type="button"
                data-testid="push-enable"
                onClick={onEnable}
                disabled={busy || permission === "denied"}
                className="btn-primary rounded-full"
              >
                {busy ? "Enabling…" : "Enable notifications"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
